import React, { useState, useEffect } from "react";
import { supabase } from "../utils/supabaseClient";
import { ToastContainer, toast } from "react-toastify";
import { Image } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import "react-toastify/dist/ReactToastify.css";
import "../styles/PartnerManagement.css";

const PartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon_url: "",
    banner_url: "",
    url: "",
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const { data: partnersData, error: partnersError } = await supabase
        .from("partners")
        .select("*")
        .order("name");

      if (partnersError) throw partnersError;
      setPartners(partnersData);
    } catch (error) {
      setError(error.message);
      toast.error("Error fetching partners");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
    }
  };

  const uploadImage = async (file, folder) => {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("images").getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      throw new Error(`Error uploading ${folder}: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let iconUrl = formData.icon_url;
      let bannerUrl = formData.banner_url;

      if (iconFile) {
        iconUrl = await uploadImage(iconFile, "partner-icons");
      }
      if (bannerFile) {
        bannerUrl = await uploadImage(bannerFile, "partner-banners");
      }

      if (isEditing) {
        const { error: updateError } = await supabase
          .from("partners")
          .update({
            name: formData.name,
            description: formData.description,
            icon_url: iconUrl,
            banner_url: bannerUrl,
            url: formData.url,
          })
          .eq("id", currentPartner.id);

        if (updateError) throw updateError;
        toast.success("Partner updated successfully!");
      } else {
        const { error: partnerError } = await supabase.from("partners").insert([
          {
            name: formData.name,
            description: formData.description,
            icon_url: iconUrl,
            banner_url: bannerUrl,
            url: formData.url,
          },
        ]);

        if (partnerError) throw partnerError;
        toast.success("Partner created successfully!");
      }

      resetForm();
      fetchPartners();
    } catch (error) {
      setError(error.message);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleEdit = (partner) => {
    setCurrentPartner(partner);
    setFormData({
      name: partner.name,
      description: partner.description,
      icon_url: partner.icon_url,
      banner_url: partner.banner_url,
      url: partner.url,
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this partner?")) {
      try {
        const { error } = await supabase.from("partners").delete().eq("id", id);

        if (error) throw error;
        toast.success("Partner deleted successfully!");
        fetchPartners();
      } catch (error) {
        setError(error.message);
        toast.error("An error occurred while deleting the partner.");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      icon_url: "",
      banner_url: "",
      url: "",
    });
    setIconFile(null);
    setBannerFile(null);
    setIsEditing(false);
    setCurrentPartner(null);
  };

  if (loading) return <LoadingSpinner size="large" />;
  if (error)
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <h2>{isEditing ? "Edit Partner" : "Add New Partner"}</h2>

      <form onSubmit={handleSubmit} className="partner-form">
        <div className="form-group">
          <label htmlFor="name">Partner Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="url">Website URL</label>
          <input
            type="url"
            id="url"
            name="url"
            value={formData.url}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="banner">
            Partner Banner
            <Image size={20} className="input-icon" />
          </label>
          <input
            type="file"
            id="banner"
            accept="image/*"
            onChange={handleBannerChange}
            className="file-input"
          />
          {formData.banner_url && (
            <div className="current-banner">
              <img
                src={formData.banner_url}
                alt="Current banner"
                className="banner-preview"
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="icon">
            Partner Icon
            <Image size={20} className="input-icon" />
          </label>
          <input
            type="file"
            id="icon"
            accept="image/*"
            onChange={handleIconChange}
            className="file-input"
          />
          {formData.icon_url && (
            <div className="current-icon">
              <img
                src={formData.icon_url}
                alt="Current icon"
                className="icon-preview"
              />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button">
            {isEditing ? "Update Partner" : "Add Partner"}
          </button>
          {isEditing && (
            <button type="button" onClick={resetForm} className="cancel-button">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="partners-list">
        <h3>Existing Partners</h3>
        <div className="partners-grid">
          {partners.map((partner) => (
            <div key={partner.id} className="partner-card">
              <div className="partner-card-content">
                {partner.banner_url && (
                  <div className="partner-banner">
                    <img
                      src={partner.banner_url}
                      alt={`${partner.name} banner`}
                      className="banner-image"
                    />
                  </div>
                )}
                <div className="partner-header">
                  <div className="partner-icon-container">
                    {partner.icon_url && (
                      <img
                        src={partner.icon_url}
                        alt={partner.name}
                        className="partner-icon"
                      />
                    )}
                  </div>
                  <h4>{partner.name}</h4>
                </div>
                <p>{partner.description}</p>
                <div className="partner-links">
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-link"
                  >
                    Visit Website
                  </a>
                </div>
                <div className="partner-actions">
                  <button
                    onClick={() => handleEdit(partner)}
                    className="edit-button"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PartnerManagement;
